import { styled, motion, colors } from '../../styles/base/_allBase.styles'

export const BlogSectionWrapper = styled(motion.article)`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'number'
    'heading'
    'image'
    'copy'
    'cta';
  gap: 1.8rem;
  width: min(100%, 112rem);
  margin: 0 auto;
  padding: clamp(5rem, 7vw, 8rem) 0;
  border-top: 1px solid ${colors.neutralPalette.greyLight};

  &[data-layout='intro'] {
    width: min(100%, 82rem);
    padding-top: clamp(4rem, 6vw, 6.5rem);
    border-top: 0;
  }

  &[data-layout='prose'],
  &[data-layout='closing'],
  &[data-layout='business'] {
    width: min(100%, 78rem);
  }

  &[data-layout='closing'] {
    margin-bottom: clamp(3rem, 6vw, 6rem);
    border-top: 2px solid ${colors.warmPalette.primary};
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(12, minmax(0, 1fr));
    column-gap: clamp(2rem, 3vw, 4rem);
    row-gap: 2rem;
    grid-template-areas: none;

    > * { min-width: 0; }

    &[data-layout='intro'],
    &[data-layout='prose'],
    &[data-layout='closing'] {
      grid-template-areas:
        'number number number number number number number number number number number number'
        'heading heading heading heading heading heading heading heading heading heading . .'
        'copy copy copy copy copy copy copy copy copy copy . .'
        'cta cta cta cta cta cta cta cta cta cta . .';
    }

    &[data-layout='split'] {
      grid-template-areas:
        'number number number number number number number number number number number number'
        'image image image image image image heading heading heading heading heading heading'
        'image image image image image image copy copy copy copy copy copy'
        'image image image image image image cta cta cta cta cta cta';
      align-items: start;
    }

    &[data-layout='feature'] {
      grid-template-areas:
        'number number number number number number number number number number number number'
        'heading heading heading heading heading heading heading heading heading . . .'
        'image image image image image image image image image image image image'
        '. . copy copy copy copy copy copy copy copy . .'
        '. . cta cta cta cta cta cta cta cta . .';
    }

    &[data-layout='business'] {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-areas:
        'heading'
        'image'
        'copy'
        'cta';
    }
  }

  @media (max-width: 899px) {
    padding: clamp(4rem, 10vw, 6rem) 0;
  }
`

export const EditorialNumber = styled.span`
  grid-area: number;
  color: ${colors.warmPalette.textAccent};
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  line-height: 1;
`

export const HeadingBlock = styled.header`
  grid-area: heading;
  align-self: start;
  max-width: 86rem;
`

export const SectionHeading = styled.h2`
  && {
    margin: 0;
  }
  color: ${colors.warmPalette.primaryDark};
  font-family: 'Whyte-Bold', sans-serif;
  font-size: clamp(3rem, 3.4vw, 4.8rem);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -0.03em;
  text-wrap: balance;

  ${BlogSectionWrapper}[data-layout='intro'] & {
    max-width: 19ch;
    font-size: clamp(3.6rem, 4.6vw, 5.8rem);
  }

  ${BlogSectionWrapper}[data-layout='business'] & {
    font-size: clamp(2.8rem, 3.2vw, 4.2rem);
  }
`

export const SectionDeck = styled.p`
  && {
    margin: 1.2rem 0 0;
  }
  max-width: 58ch;
  color: ${colors.neutralPalette.greyDark};
  font-size: clamp(1.7rem, 1.7vw, 2rem);
  line-height: 1.45;
`

export const CopyBlock = styled.div`
  grid-area: copy;
  align-self: start;
  max-width: 68ch;

  p {
    margin-top: 0;
    font-size: clamp(1.7rem, 1.35vw, 1.9rem);
    line-height: 1.75;
  }

  p:first-child {
    color: ${colors.warmPalette.primaryDark};
    font-size: clamp(1.8rem, 1.55vw, 2.1rem);
    line-height: 1.65;
  }

  li {
    margin-bottom: 0.8rem;
    font-size: 1.7rem;
    line-height: 1.65;
  }

  blockquote {
    margin: 3rem 0;
    padding: 0 0 0 2.4rem;
    border-left: 3px solid ${colors.warmPalette.tertiary};
    color: ${colors.warmPalette.primaryDark};
    font-family: 'Whyte-Bold', sans-serif;
    font-size: clamp(2.3rem, 2.7vw, 3.5rem);
    line-height: 1.25;
  }
`

export const ImageWrapper = styled.figure`
  grid-area: image;
  position: relative;
  width: 100%;
  min-height: 30rem;
  aspect-ratio: 4 / 3;
  margin: 0;
  overflow: hidden;
  background: ${colors.coolPalette.neutralLight};

  img {
    object-fit: cover;
    transition: transform 500ms ease-out;
  }

  ${BlogSectionWrapper}:hover & img { transform: scale(1.01); }

  @media (prefers-reduced-motion: reduce) {
    img { transition: none; }
    ${BlogSectionWrapper}:hover & img { transform: none; }
  }

  @media (min-width: 900px) {
    ${BlogSectionWrapper}[data-layout='split'] & {
      min-height: 52rem;
      aspect-ratio: 4 / 5;
    }
    ${BlogSectionWrapper}[data-layout='feature'] & {
      min-height: 50rem;
      aspect-ratio: 16 / 8;
    }
  }
`

export const CenteredButton = styled(motion.div)`
  grid-area: cta;
  display: flex;
  justify-content: flex-start;
  align-self: start;
  width: 100%;
  margin-top: 0.5rem;
`
